<script lang="ts">
  import ModalShell from './ModalShell.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import Icon from './Icon.svelte';
  import { deleteGroup, reorderGroups } from './api';
  import { createListDrag } from './list-drag.svelte';
  import type { GroupView } from '~/shared/api.js';

  let {
    tripToken,
    groups,
    poiCounts,
    onEdit,
    onDeleted,
    onReorder,
    onClose,
  }: {
    tripToken: string;
    groups: GroupView[];
    poiCounts: Map<number, number>;
    /** Open the existing GroupModal in edit mode. Parent handles. */
    onEdit: (group: GroupView) => void;
    /** Reuses TripPage.onGroupDeleted — drops the row from `groups`
     *  and detaches its membership from POIs. */
    onDeleted: (id: number) => void;
    /** New order committed via drag. Parent persists it locally so
     *  the filter row chips update; this component fires the server
     *  PATCH in the background. */
    onReorder: (groups: GroupView[]) => void;
    onClose: () => void;
  } = $props();

  const drag = createListDrag({
    ignoreSelector: '.action',
    onCommit(fromIdx, toIdx) {
      const next = [...groups];
      const moved = next.splice(fromIdx, 1)[0]!;
      next.splice(toIdx, 0, moved);
      onReorder(next);
      // Best-effort server persist. On failure, the parent owns the
      // local state — refetch on next trip load reconciles.
      reorderGroups(
        tripToken,
        next.map((g) => g.id),
      ).catch((e) => console.error('Failed to reorder groups:', e));
    },
  });

  let confirmingDeleteId: number | null = $state(null);
  let deletingId: number | null = $state(null);

  const confirmingGroup = $derived(
    confirmingDeleteId === null
      ? null
      : (groups.find((g) => g.id === confirmingDeleteId) ?? null),
  );

  async function confirmDelete() {
    if (confirmingDeleteId === null) return;
    const id = confirmingDeleteId;
    confirmingDeleteId = null;
    deletingId = id;
    try {
      await deleteGroup(tripToken, id);
      onDeleted(id);
    } catch (e) {
      console.error('Failed to delete group:', e);
    } finally {
      deletingId = null;
    }
  }
</script>

<ModalShell title="Manage groups" {onClose} maxWidth={460}>
  {#if groups.length === 0}
    <p class="empty">You haven't created any groups yet.</p>
  {:else}
    <ul class="list">
      {#each groups as g, idx (g.id)}
        {@const count = poiCounts.get(g.id) ?? 0}
        <li
          bind:this={() => drag.getRowEl(idx), (el) => drag.setRowEl(idx, el)}
          class:dragging={drag.isDraggingRow(idx)}
          style={drag.rowStyle(idx)}
          onpointerdown={(e) => drag.onDown(e, idx)}
          onpointermove={drag.onMove}
          onpointerup={drag.onUp}
          onpointercancel={drag.onCancel}
        >
          <span class="grip" aria-hidden="true">
            <Icon name="grip-vertical" size={14} />
          </span>
          <span class="dot dot--{g.tint}" aria-hidden="true"></span>
          <span class="name truncate">{g.name}</span>
          <span class="count">{count} {count === 1 ? 'place' : 'places'}</span>
          <button
            type="button"
            class="action"
            aria-label="Edit {g.name}"
            title="Edit"
            onclick={() => onEdit(g)}
          >
            <Icon name="pencil" size={14} />
          </button>
          <button
            type="button"
            class="action action--destructive"
            aria-label="Delete {g.name}"
            title="Delete"
            disabled={deletingId === g.id}
            onclick={() => (confirmingDeleteId = g.id)}
          >
            <Icon name="trash" size={14} />
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</ModalShell>

{#if confirmingGroup}
  <ConfirmDialog
    title="Delete group?"
    message={`Delete the "${confirmingGroup.name}" group? Places in this group stay in your trip.`}
    confirmLabel="Delete group"
    destructive
    onConfirm={confirmDelete}
    onCancel={() => (confirmingDeleteId = null)}
  />
{/if}

<style>
  .empty {
    margin: 0;
    color: var(--color-muted);
    font-size: 14px;
    text-align: center;
    padding: 1rem 0;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-hairline-soft);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  .list li {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr) auto auto auto;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem 0.875rem;
    min-height: 48px;
    touch-action: none;
    background: var(--color-canvas);
    transition: box-shadow 120ms ease;
  }
  .list li.dragging {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 24px;
    border-radius: var(--radius-sm);
  }
  .grip {
    display: inline-flex;
    color: var(--color-muted);
    cursor: grab;
  }
  .list li.dragging .grip,
  .list li:active .grip { cursor: grabbing; }
  .list li + li { border-top: 1px solid var(--color-hairline-soft); }
  .dot {
    width: 8px;
    height: 8px;
    flex: none;
  }
  .name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
  }
  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .count {
    font-size: 12px;
    color: var(--color-muted);
    white-space: nowrap;
  }
  .action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    cursor: pointer;
    color: var(--color-muted);
  }
  .action:hover {
    background: var(--color-surface-soft);
    color: var(--color-ink);
  }
  .action--destructive:hover { color: var(--color-error); }
  .action:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
</style>
